import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, CloudRain, Copy, Download, Gift, Link2, Receipt, ShoppingBag, Trash2 } from "lucide-react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Panel, WaButton, inputCls, itDate } from "@/components/kit";
import { EditableRows } from "@/components/EditableRows";
import { API, api, errMsg, fmtMoney } from "@/lib/api";

const STATO = { richiesta: "bg-[#FEF3C7] text-[#92400E]", approvata: "bg-[#DCFCE7] text-[#166534]", rifiutata: "bg-[#FEE2E2] text-[#991B1B]" };

function Requests({ c, u, reload }) {
  const decide = async (id, azione) => {
    try {
      await api.post(`/contracts/${c.id}/extra-requests/${id}`, { azione });
      toast.success(azione === "approva" ? "Approvato: creato l'ordine extra (Allegato D) in bozza da far firmare" : "Richiesta rifiutata");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Extra richiesti dal cliente" testId="extra-requests-panel">
      <p className="text-sm text-[#47606B] -mt-2">Gli sposi li scelgono dall'area cliente tra i servizi del Listino segnati «Mostra nell'area cliente». Approvando si crea l'ordine extra da firmare.</p>
      {!u.requests.length && <div className="text-sm text-[#7B929C]">Nessuna richiesta.</div>}
      {u.requests.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#EAF5F7] pt-3 text-sm" data-testid={`xreq-${r.id}`}>
          <div><b>{r.nome}</b> × {r.quantita} • {fmtMoney(r.prezzo * r.quantita)}{r.note && <div className="text-xs text-[#7B929C]">{r.note}</div>}</div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase px-2 py-1 rounded-full ${STATO[r.stato]}`}>{r.stato}{r.extra_numero && ` • ${r.extra_numero}`}</span>
            {r.stato === "richiesta" && <><Button size="sm" className="rounded-full" onClick={() => decide(r.id, "approva")} data-testid={`xreq-ok-${r.id}`}>Approva</Button><Button size="sm" variant="ghost" className="rounded-full text-[#991B1B]" onClick={() => decide(r.id, "rifiuta")} data-testid={`xreq-no-${r.id}`}>Rifiuta</Button></>}
          </div>
        </div>
      ))}
    </Panel>
  );
}

function Deadlines({ c, u, reload }) {
  const save = async (rows) => {
    await api.put(`/contracts/${c.id}/scadenze`, { scadenze: rows.map((r) => ({ ...r, giorni: Number(r.giorni) || 0 })) });
    toast.success("Scadenze salvate");
    reload();
  };
  return (
    <Panel title="Scadenze per il cliente" testId="deadlines-panel">
      <p className="text-sm text-[#47606B] -mt-2">Giorni prima dell'evento. Il cliente le vede nella sua area con il conto alla rovescia e voi trovate il promemoria WhatsApp in «Oggi».</p>
      <EditableRows testId="deadlines" rows={u.scadenze} onSave={save} addLabel="Aggiungi scadenza"
        cols={[{ k: "titolo", label: "Cosa serve", w: "45%" }, { k: "giorni", label: "Giorni prima", type: "number", w: "110px" }, { k: "fatto", label: "Fatto", type: "check", w: "60px" }]} />
      <div className="text-xs text-[#7B929C]">{u.scadenze.map((s) => `${s.titolo}: ${itDate(s.data)}`).join(" • ")}</div>
    </Panel>
  );
}

function LinkRow({ url, testId, text, phone }) {
  return (
    <div className="space-y-2">
      <div className="text-xs break-all rounded-md bg-[#F5FAFB] border border-[#D4E6EC] p-2" data-testid={`${testId}-url`}>{url}</div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"))} data-testid={`${testId}-copy`}><Copy size={13} className="mr-1" /> Copia</Button>
        <WaButton phone={phone || ""} text={`${text} 👉 ${url}`} label="WhatsApp" testId={`${testId}-wa`} />
      </div>
    </div>
  );
}

async function qrCards(c, u, qrBlob) {
  const qr = await new Promise((ok) => { const fr = new FileReader(); fr.onload = () => ok(fr.result); fr.readAsDataURL(qrBlob); });
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  [[10, 10], [110, 10], [10, 150], [110, 150]].forEach(([x, y]) => {
    pdf.setDrawColor(200); pdf.rect(x, y, 90, 135);
    pdf.setFont("times", "italic"); pdf.setFontSize(20); pdf.text(c.data?.cliente_nome || "Il nostro giorno", x + 45, y + 18, { align: "center", maxWidth: 80 });
    pdf.addImage(qr, "PNG", x + 17, y + 32, 56, 56);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.text("Condividi le tue foto con noi!", x + 45, y + 102, { align: "center" });
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.text("Inquadra il codice con la fotocamera\ne carica le foto della festa", x + 45, y + 112, { align: "center" });
  });
  pdf.save(`QR foto ${c.data?.cliente_nome || c.number}.pdf`);
}

function Photos({ c, u }) {
  const [list, setList] = useState(null);
  const load = useCallback(() => api.get(`/contracts/${c.id}/photos`).then((r) => setList(r.data)), [c.id]);
  useEffect(() => {
    load();
  }, [load]);
  const url = `${window.location.origin}/foto/${u.photo_token}`;
  const cards = async () => {
    try {
      const r = await api.get(`/contracts/${c.id}/photos/qr`, { params: { origin: window.location.origin }, responseType: "blob" });
      await qrCards(c, u, r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa foto dall'album?")) return;
    try {
      await api.delete(`/contracts/${c.id}/photos/${id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title={`Foto degli ospiti (${list?.length ?? u.photos})`} testId="photos-panel">
      <p className="text-sm text-[#47606B] -mt-2">Mettete il QR sui tavoli: gli invitati caricano le foto e tutti possono vederle. Con «consenso» potete usarle per social e brochure.</p>
      <LinkRow url={url} testId="photo-link" text="📸 Carica qui le foto della festa" phone={c.data?.cliente_tel} />
      <Button size="sm" className="rounded-full" onClick={cards} data-testid="photo-qr-cards"><Download size={13} className="mr-1" /> Cartoncini QR da stampare</Button>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {(list || []).map((p) => (
          <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
            <a href={`${API}/public/foto/${u.photo_token}/${p.id}`} target="_blank" rel="noreferrer"><img src={`${API}/public/foto/${u.photo_token}/${p.id}?thumb=1`} alt="" className="aspect-square object-cover rounded-lg w-full" /></a>
            {p.consenso_marketing && <span className="absolute bottom-1 left-1 text-[9px] bg-[#166534] text-white px-1.5 rounded-full">consenso</span>}
            <button className="absolute top-1 right-1 bg-white/90 rounded-full p-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={() => del(p.id)} data-testid={`photo-del-${p.id}`}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Referral({ u, reload }) {
  const [premio, setPremio] = useState(u.premio);
  const savePremio = async () => {
    try {
      await api.put("/settings/referral", { premio });
      toast.success("Premio salvato per tutti i clienti");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const given = async (id) => {
    try {
      await api.post(`/leads/${id}/premio`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Porta un amico" testId="referral-panel">
      <div className="text-sm">{u.referral_code ? <>Codice del cliente: <b data-testid="referral-code">{u.referral_code}</b></> : "Il codice si crea quando il contratto è firmato e il cliente apre la sua area."}</div>
      <div className="flex gap-2"><input className={inputCls} value={premio} onChange={(e) => setPremio(e.target.value)} placeholder="Premio per chi porta un amico (es. bottiglia di benvenuto)" data-testid="referral-premio" /><Button size="sm" variant="outline" className="rounded-full" onClick={savePremio} data-testid="referral-premio-save">Salva</Button></div>
      {u.referrals.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-sm border-t border-[#EAF5F7] pt-2" data-testid={`referral-${r.id}`}>
          <span>{r.nome} • {r.stage}</span>
          {r.premio_consegnato ? <span className="text-xs text-[#166534]">premio consegnato</span> : r.stage === "contratto" ? <Button size="sm" className="rounded-full" onClick={() => given(r.id)} data-testid={`referral-give-${r.id}`}><Gift size={13} className="mr-1" /> Premio consegnato</Button> : <span className="text-xs text-[#7B929C]">in trattativa</span>}
        </div>
      ))}
    </Panel>
  );
}

function Meteo({ c, u }) {
  const [m, setM] = useState(u.meteo);
  const save = async () => {
    try {
      await api.put(`/contracts/${c.id}/meteo`, m);
      toast.success("Meteo e piano B salvati");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Evento all'aperto e piano B" testId="meteo-panel">
      <p className="text-sm text-[#47606B] -mt-2"><CloudRain size={13} className="inline mr-1" />Se l'evento è all'aperto, 5 giorni prima trovate in «Oggi» l'avviso quando le previsioni danno pioggia o vento forte.</p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={m.aperto} onChange={(e) => setM({ ...m, aperto: e.target.checked })} data-testid="meteo-aperto" /> Evento (o parte dell'evento) all'aperto</label>
      {m.aperto && <textarea rows={2} className={inputCls} value={m.piano_b} onChange={(e) => setM({ ...m, piano_b: e.target.value })} placeholder="Piano B: es. sala interna Afrodite, mappa tavoli «Interno 120», gazebo dal fornitore…" data-testid="meteo-piano-b" />}
      <Button size="sm" className="rounded-full" onClick={save} data-testid="meteo-save">Salva</Button>
    </Panel>
  );
}

function FinalBill({ c, u, reload }) {
  const create = async () => {
    try {
      const r = await api.post(`/contracts/${c.id}/consuntivo`);
      toast.success(`Conto finale ${r.data.numero} creato: compilalo in «Allegato D» e mandalo in firma`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const b = u.consuntivo;
  return (
    <Panel title="Conto finale a fine evento" testId="final-bill-panel">
      <p className="text-sm text-[#47606B] -mt-2"><Receipt size={13} className="inline mr-1" />Ore extra, bottiglie in più, coperti aggiuntivi, rotture e danni. Il cliente lo firma e l'importo si aggiunge al saldo nei Pagamenti.</p>
      {b ? <div className="text-sm" data-testid="final-bill-status">Allegato <b>{b.numero}</b> • {b.status === "firmato" ? "firmato: importo aggiunto al saldo" : b.status === "bozza" ? "in bozza: compilalo in «Allegato D»" : "inviato in firma"}</div>
        : <Button size="sm" className="rounded-full" disabled={c.status !== "firmato"} onClick={create} data-testid="final-bill-create">{c.status === "firmato" ? "Crea conto finale" : "Disponibile dopo la firma del contratto"}</Button>}
    </Panel>
  );
}

export function UniquePanel({ c }) {
  const [u, setU] = useState(null);
  const load = useCallback(() => api.get(`/contracts/${c.id}/unique`).then((r) => setU(r.data)), [c.id]);
  useEffect(() => {
    load();
  }, [load]);
  if (!u) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-[#47606B]"><ShoppingBag size={15} /> Extra, scadenze, fornitori, foto e passaparola</div>
      <Requests c={c} u={u} reload={load} />
      <Deadlines c={c} u={u} reload={load} />
      {!/AFRODITE/i.test(c.data?.sede || "") && <Meteo c={c} u={u} />}
      <FinalBill c={c} u={u} reload={load} />
      <Panel title="Link per i fornitori" testId="supplier-link-panel">
        <p className="text-sm text-[#47606B] -mt-2"><Link2 size={13} className="inline mr-1" />Fiorista, fotografo, DJ… vedono data, orari di arrivo, scaletta e referenti. Non vedono prezzi né dati del cliente.</p>
        <LinkRow url={`${window.location.origin}/fornitori/${u.supplier_token}`} testId="supplier-link" text={`📋 Info evento ${itDate(c.data?.evento_data)}`} />
      </Panel>
      <Photos c={c} u={u} />
      <Referral u={u} reload={load} />
      <p className="text-xs text-[#7B929C] flex items-center gap-1"><Camera size={12} /> Le foto restano visibili a chi ha il link.</p>
    </div>
  );
}

export function PendingExtras() {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/extra-requests/pending").then((r) => setList(r.data));
  }, []);
  if (!list.length) return null;
  return (
    <Panel title="Extra richiesti dai clienti" testId="pending-extras-panel">
      {list.map((r) => <a key={r.id} href={`/contratti/${r.contract_id}`} className="flex justify-between text-sm border-t border-[#EAF5F7] pt-2 hover:text-[#0B6E8F]" data-testid={`pending-extra-${r.id}`}><span><b>{r.cliente}</b> • {r.nome} × {r.quantita}</span><span>{fmtMoney(r.prezzo * r.quantita)}</span></a>)}
    </Panel>
  );
}
